import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Eye, Users, TrendingUp, Calendar } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RANGES = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
];

const StatCard = ({ icon: Icon, label, value, sub, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-center gap-3">
        <div className="bg-emerald-50 rounded-lg p-2.5">
          <Icon className="h-5 w-5 text-emerald-600" />
        </div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

export const AnalyticsPanel = () => {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/admin/analytics/summary?days=${days}`,
        { withCredentials: true }
      );
      setData(data);
    } catch (e) {
      console.error("Analytics fetch failed", e);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Build a simple bar chart from the daily counts
  const maxDailyViews = Math.max(1, ...(data?.daily || []).map((d) => d.views));

  return (
    <div className="space-y-6" data-testid="analytics-panel">
      {/* Range selector */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold text-gray-900">Website Traffic</h2>
        <div className="flex gap-2">
          {RANGES.map((r) => (
            <button
              key={r.days}
              type="button"
              onClick={() => setDays(r.days)}
              data-testid={`range-${r.days}d`}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                days === r.days
                  ? "bg-emerald-600 text-white"
                  : "bg-stone-100 text-gray-700 hover:bg-stone-200"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-gray-500 text-center py-8">Loading analytics…</p>
      ) : !data ? (
        <p className="text-gray-500 text-center py-8">No data available.</p>
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard
              icon={Eye}
              label={`Page views (last ${days}d)`}
              value={data.total_views.toLocaleString()}
              testId="stat-total-views"
            />
            <StatCard
              icon={Users}
              label="Unique visitors"
              value={data.unique_sessions.toLocaleString()}
              sub="Distinct browsers/sessions"
              testId="stat-unique-sessions"
            />
            <StatCard
              icon={Calendar}
              label="Views today"
              value={data.today_views.toLocaleString()}
              testId="stat-today-views"
            />
          </div>

          {/* Daily bar chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <TrendingUp className="h-4 w-4 text-emerald-600" />
                Daily Views
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.daily.length === 0 ? (
                <p className="text-gray-500 text-center py-8 text-sm">
                  No traffic recorded yet in this window. Share your link to start seeing data.
                </p>
              ) : (
                <div className="flex items-end gap-2 h-48" data-testid="daily-chart">
                  {data.daily.map((d) => {
                    const height = (d.views / maxDailyViews) * 100;
                    return (
                      <div
                        key={d.date}
                        className="flex-1 flex flex-col items-center gap-1 min-w-0"
                        title={`${d.date}: ${d.views} views`}
                      >
                        <span className="text-xs font-semibold text-gray-700">{d.views}</span>
                        <div
                          className="w-full bg-emerald-500 rounded-t transition-all"
                          style={{ height: `${Math.max(2, height)}%` }}
                        />
                        <span className="text-[10px] text-gray-400 -rotate-45 origin-top-right whitespace-nowrap">
                          {d.date.slice(5)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top pages */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Top Pages</CardTitle>
            </CardHeader>
            <CardContent>
              {data.top_pages.length === 0 ? (
                <p className="text-gray-500 text-sm">No page views recorded.</p>
              ) : (
                <div className="space-y-2" data-testid="top-pages">
                  {data.top_pages.map((p) => {
                    const pct = (p.views / data.total_views) * 100;
                    return (
                      <div key={p.path} className="flex items-center gap-3">
                        <span className="font-mono text-sm text-gray-700 w-40 truncate">
                          {p.path}
                        </span>
                        <div className="flex-1 bg-stone-100 rounded-full h-5 overflow-hidden relative">
                          <div
                            className="bg-emerald-500 h-full rounded-full"
                            style={{ width: `${pct}%` }}
                          />
                          <span className="absolute inset-0 flex items-center justify-end pr-2 text-xs font-semibold text-gray-700">
                            {p.views.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <p className="text-xs text-gray-500 text-center">
            Tracking is privacy-friendly: anonymous per-browser session IDs only, no cookies, no third-party tracking.
          </p>
        </>
      )}
    </div>
  );
};
